// Webhook rows for platform.lawline.se. Mirrors the selects in apps/api
// features/platform/webhooks/webhooks.repository.ts. No response ever carries a stored secret;
// `secret` appears once, on create and on rotate.

export const WEBHOOK_EVENT_TYPES = ["run.completed", "run.failed", "run.cancelled"] as const;
export type WebhookEventType = (typeof WEBHOOK_EVENT_TYPES)[number];

export const WEBHOOK_RUN_KINDS = ["review", "answer"] as const;
export type WebhookRunKind = (typeof WEBHOOK_RUN_KINDS)[number];

export interface WebhookEndpoint {
	id: string;
	url: string;
	description: string | null;
	eventTypes: WebhookEventType[];
	// Null means every kind, including kinds added later.
	runKinds: WebhookRunKind[] | null;
	status: "enabled" | "disabled";
	disabledReason: "manual" | "gone_410" | "failing" | null;
	disabledAt: string | null;
	// Until then the secret a rotation replaced also signs.
	previousSecretExpiresAt: string | null;
	lastSuccessAt: string | null;
	lastFailureAt: string | null;
	failingSince: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface WebhookEndpointWithSecret extends WebhookEndpoint {
	secret: string;
}

// What the endpoint form holds. Every kind ticked stands for null on the wire.
export interface WebhookEndpointFields {
	url: string;
	description: string;
	eventTypes: WebhookEventType[];
	runKinds: WebhookRunKind[];
}

// The PATCH body: only the fields that change.
export interface WebhookEndpointChange {
	url?: string;
	description?: string | null;
	eventTypes?: WebhookEventType[];
	runKinds?: WebhookRunKind[] | null;
	enabled?: boolean;
}

export const WEBHOOK_DELIVERY_STATUSES = ["pending", "succeeded", "failed", "cancelled"] as const;
export type WebhookDeliveryStatus = (typeof WEBHOOK_DELIVERY_STATUSES)[number];

export interface WebhookDelivery {
	id: string;
	eventId: string;
	// A run event type, or webhook.test.
	eventType: string;
	runId: string | null;
	status: WebhookDeliveryStatus;
	attemptCount: number;
	nextAttemptAt: string | null;
	lastStatusCode: number | null;
	lastErrorCode: string | null;
	succeededAt: string | null;
	createdAt: string;
	// A send asked for by hand that has not been made yet.
	manualPending: boolean;
}

export interface WebhookDeliveryPage {
	data: WebhookDelivery[];
	hasMore: boolean;
}

export interface WebhookAttempt {
	attempt: number;
	trigger: "automatic" | "manual";
	startedAt: string;
	durationMs: number;
	statusCode: number | null;
	errorCode: string | null;
	responseExcerpt: string | null;
}

export interface WebhookDeliveryDetail extends WebhookDelivery {
	payload: Record<string, unknown>;
	attempts: WebhookAttempt[];
}

// One event of the organization and its delivery to each endpoint it reached that still exists.
export interface WebhookEvent {
	id: string;
	type: string;
	runId: string | null;
	createdAt: string;
	deliveries: Array<
		Pick<
			WebhookDelivery,
			| "id"
			| "status"
			| "attemptCount"
			| "nextAttemptAt"
			| "lastStatusCode"
			| "lastErrorCode"
			| "manualPending"
		> & { endpointId: string; endpointUrl: string }
	>;
}

export interface WebhookEventPage {
	data: WebhookEvent[];
	hasMore: boolean;
}

export interface QueuedWebhookDelivery {
	id: string;
	eventId: string;
	status: "pending";
}
